import React, { useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";

import { dataActions } from '@/store/actions/data.actions';

import { CanHandleDepartments } from '@/utils/permissions/permissions';

import AddLetterDatePicker from '@/views/pages/AddLetter/elements/AddLetterDatePicker';
import AddLetterDeptAutoSuggest from '@/views/pages/AddLetter/elements/AddLetterDeptAutoSuggest';

import { DropdownIndicator, NoOptionsMessage } from '@/views/components/CustomSelect/CustomSelect';

import Spinner from '@/views/components/Spinner/Spinner';
import ModalWrapperChildren from "@/views/components/Modal/ModalWrapperChildren";

const defaultValues = {
	title: '',
	filename: '',
	titleType: '',
	category: '',
	letterDate: null,
	author: {
		forename: '',
		surname: '',
	},
	recipient: '',
	department: { id: null },
};

export const EditLetterForm = ({ letterData, saveData, cancelForm }) => {
	const dispatch = useDispatch();
	const { recipients, allTitleTypes, categories } = useSelector(state => state.data);
	const { dict, mode } = useSelector((state) => state.app);
	const { user } = useSelector((state) => state.auth);

	const [ selectTitleTypes, setSelectTitleTypes ] = useState([]);
	const [ selectRecipients, setSelectRecipients ] = useState([]);
	const [ selectCategories, setSelectCategories ] = useState([]);

	const [ editRequestIsPending, setEditRequestIsPending ] = useState(false);

	const { register, handleSubmit, setValue, control, watch } = useForm({ defaultValues: defaultValues });

	const [ canSubmit, setCanSubmit ] = useState(true);

	const titleString = watch("title");
	const { id: deptId } = watch('department');

	useEffect(() => {
		if (!deptId || deptId.length < 1 || titleString.length < 1) {
			setCanSubmit(false);
		} else {
			setCanSubmit(true);
		}
	}, [ deptId, titleString ]);

	useEffect(() => {
		if (letterData) {
			if (letterData.titleType && selectTitleTypes) {
				setValue("titleType", (selectTitleTypes.filter(item => item.value === letterData.titleType)[0]));
			}
			if (letterData.title) {
				setValue('title', letterData.title);
			}
			if (letterData.date) {
				setValue("letterDate", letterData.date);
			}
			if (letterData.author) {
				setValue('author', letterData.author);
			}
			if (letterData.department) {
				setValue('department', letterData.department);
			}
			if (letterData.recipient && selectRecipients && selectRecipients.length) {
				setTimeout(function () {
					setValue("recipient", selectRecipients.filter(item => item.value === letterData.recipient.code)[0]);
				}, 100);
			}
			if (letterData.category && selectCategories && selectCategories.length) {
				setTimeout(function () {
					setValue("category", selectCategories.filter(item => item.value === letterData.category.code)[0]);
				}, 100);
			}
		}
		// eslint-disable-next-line
    }, [letterData, selectTitleTypes, selectRecipients, selectCategories]);

	useEffect(() => {
		if (allTitleTypes) {
			const selectTitleTypesArray = [
				...allTitleTypes.map(item => ({
					value: item, label: dict.addLetter[item],
				})) ];
			setSelectTitleTypes(selectTitleTypesArray);
		}
		// eslint-disable-next-line
    }, [allTitleTypes]);

	const onSubmit = (data) => {
		setEditRequestIsPending(true);
		saveData(data);
	};

	useEffect(() => {
		if (!recipients) dispatch(dataActions.getRecipients());
		if (!allTitleTypes) dispatch(dataActions.getAllTitleTypes());
		if (!categories) dispatch(dataActions.getLetterCategories());
		// eslint-disable-next-line
    }, []);

	useEffect(() => {
		if (recipients) {
			setSelectRecipients(recipients.map(item => ({
				value: item.code, label: item.label,
			})));
		}
		// eslint-disable-next-line
    }, [recipients]);

	useEffect(() => {
		if (categories) {
			setSelectCategories(categories.map(item => ({
				value: item.code, label: item.label,
			})));
		}
		// eslint-disable-next-line
    }, [categories]);

	const colourStyles = {
		control: styles => ({ ...styles, backgroundColor: '#FCFCFC', borderColor: '#DADADA', maxHeight: '32px', height: '32px', minHeight: '0' }),
		valueContainer: styles => ({ ...styles, padding: "0px 8px", maxHeight: '32px', height: '32px' }),
		indicatorsContainer: styles => ({ ...styles, maxHeight: '32px', height: '32px' }),
	};
	return (
		<>
			{ letterData && (
				<ModalWrapperChildren
					onClose={ cancelForm }
					modalVisible={ true }
					title={ dict.addLetter.editTitle }
				>
					{editRequestIsPending && (
						<div className="letter__edit-metadata--spinner">
							<Spinner />
						</div>
					)}
					<p className="addletter__comment--grey addletter__comment--edit-metadata-required">{dict.addLetter.fieldsRequired}</p>
					<form
						className="addletter__form"
						style={ { width: "100%" } }
					>
						<section className="addletter__section--title">
							<label>{dict.addLetter.title} *</label>
							<input { ...register("title") } />
						</section>

						<section
							style={ { display: (titleString.length > 0 ? "block" : "none") } }
							className="addletter__section--titletype"
						>

							<label>{dict.addLetter.titleType}</label>
							<Controller
								name="titleType"
								render={ ({ field }) => <Select
									options={ selectTitleTypes }
									styles={ colourStyles }
									isSearchable={ false }
									classNamePrefix="letters__searchbar--filters-select"
									components={ {
										DropdownIndicator,
										NoOptionsMessage,
										IndicatorSeparator: () => null,
									} }
									{ ...field }
								/> }
								control={ control }
							/>
							<div className="addletter__title-type-wrapper">
								<div className="addletter__title-type">
									<i className="icon-circle-info" />
									{dict.addLetter.titleTypes}
								</div>
								<ul className="addletter__title-type--list">
									<li>{dict.addLetter.titleTypesDescription.original}</li>
									<li>{dict.addLetter.titleTypesDescription.given}</li>
									<li>{dict.addLetter.titleTypesDescription.incipit}</li>
								</ul>
							</div>

						</section>
						<section className="addletter__section--date">
							<label>{dict.addLetter.letterDate}</label>
							<AddLetterDatePicker
								setValue={ setValue }
								propsStartDate={ letterData.date }
							/>
							<input
								style={ { display: "none" } }
								{ ...register("letterDate") }
							/>
						</section>
						<section className="addletter__section--author">
							<label>{dict.addLetter.letterAuthor}</label>
							<div className='addletter__section--author-info'>{dict.addLetter.letterAuthorAdditionalInfo}</div>
							<div className='addletter__section--name-surname'>
								<div>
									<label>{ dict.addLetter.forename }</label>
									<input
										className='addletter__section--author-input'
										{ ...register('author.forename') }
									/>
								</div>
								<div>
									<label>{ dict.addLetter.surname }</label>
									<input
										className='addletter__section--author-input'
										{ ...register('author.surname') }
									/>
								</div>
							</div>
						</section>
						<section
							className="addletter__section--titletype"
						>
							<label>{dict.addLetter.category}</label>
							<Controller
								name="category"
								render={ ({ field }) => <Select
									options={ selectCategories }
									styles={ colourStyles }
									isSearchable={ false }
									classNamePrefix="letters__searchbar--filters-select"
									components={ {
										DropdownIndicator,
										NoOptionsMessage,
										IndicatorSeparator: () => null,
									} }
									{ ...field }
								/> }
								control={ control }
							/>
						</section>
						<section className="addletter__section--department">
							<AddLetterDeptAutoSuggest
								label={ dict.addLetter.department }
								setValueExt={ setValue }
								defaultValue={ letterData?.department ?
									{
										value: letterData.department.id,
										label: letterData.department.name,
									} : null
								}
								disabled={ !CanHandleDepartments(user.role, mode) }
							/>
							<input
								style={ { display: "none" } }
								{ ...register("department.id") }
							/>
						</section>
						<section className="addletter__section--recipient">
							<label>{dict.addLetter.recipient}</label>
							<Controller
								name="recipient"
								render={ ({ field }) => <Select
									options={ selectRecipients }
									styles={ colourStyles }
									isSearchable={ false }
									classNamePrefix="letters__searchbar--filters-select"
									components={ {
										DropdownIndicator,
										NoOptionsMessage,
										IndicatorSeparator: () => null,
									} }
									{ ...field }
								/> }
								control={ control }
							/>
						</section>
						<hr className="modal-children__line" />

						<section className="addletter__edit-metadata-buttons">
							<div
								className="button__std"
								onClick={ cancelForm }
							>
								{dict.cancel}
							</div>
							<div
								className={ `button__blue${canSubmit ? "" : " disabled"}` }
								onClick={ () => handleSubmit(onSubmit)() }
							>{dict.saveChanges}
							</div>
						</section>
					</form>
				</ModalWrapperChildren>
			)}
		</>
	);
};

export default memo(EditLetterForm);
